import axios from 'axios';
import moment from 'moment';
import { logout } from '@context/user';
import { get as _get, endsWith, indexOf, first, last, includes } from 'lodash';
import Log from './Log';
import { replace } from 'connected-react-router';
import { router } from '../routes/router';
import { globalT, globalTOrPtBr, stringifyData, stringifyError, isStoreReviewBuild } from '../helpers/helpers';
import { appVersionActions, isStoreReview } from '../redux/modules/appVersion';
import { appControlTypes } from '../redux/modules/appControl';
import Bugsnag from '@bugsnag/js';
import { actionTypes } from '../redux/modules/auth';
import { requestMetadata, routeTemplate } from './bugsnagPrivacy';

export function get(route, params = {}) {
  const url = route.path;
  Log.debug('API: GET from', url, 'params:', stringifyData(params.query || ''));
  const config = {
    data: params.data || {}, // hack to keep Content-Type header in request: https://github.com/mzabriskie/axios/issues/86#issuecomment-139638284
    onDownloadProgress: params.onDownloadProgress || (() => {}),
    params: params.query,
    headers: params.headers || {},
    ['api-fallback']: route.fallback, // eslint-disable-line
    // Statuses the caller handles itself (e.g. getPatient's 404), or a function of the status and the API's answer
    // (see isHandled): no report, no fallback page
    ['api-handled']: params.handledStatuses, // eslint-disable-line
    // Optional: a way to cancel the request (an AbortController's signal) and a limit in ms. A request
    // that runs out of time fails with ETIMEDOUT, so ECONNABORTED keeps meaning an abort (wasAborted)
    signal: params.signal,
    ...(params.timeout ? { timeout: params.timeout, transitional: { clarifyTimeoutError: true } } : {}),
  };

  const firstUrl = first(String(url).split('?'));
  const lastUrl = last(String(url).split('?'));

  if (endsWith(firstUrl, 'pdf') || endsWith(lastUrl, 'pdf')) {
    config.responseType = 'arraybuffer';
    config.headers = {
      accept: 'application/pdf',
    };
  }

  return axios.get(url, config)
    .then((response) => {
      // success callback
      if (params.success) {
        params.success(response.data);
      }
      return response;
    })
    .catch((error) => {
      Log.error('Something went wrong with the GET call to ' + route.path + ' :' + stringifyError(error));
      // error callback
      if (params.error) {
        params.error(error);
      }
      // reject promise to trigger callAPI error function which calls error dispatch type
      return Promise.reject(error);
    });
}

export function post(route, params = {}) {
  Log.debug('API: POST to ' + route.path + ' query: ' + stringifyData(params.query || '') + ' data: ' + stringifyData(params.data || ''));
  return axios.post(route.path, params.data, {
    params: params.query,
    onUploadProgress: params.onUploadProgress || (() => {}),
  })
    .then((response) => {
      const data = {
        data: response.data,
        accessToken: response.headers['access-token'],
      };
      // success callback
      if (params.success) {
        params.success(data);
      }
      return response;
    })
    .catch((error) => {
      Log.error('Something went wrong with the POST call to ' + route.path + ' :' + stringifyError(error));
      // error callback
      if (params.error) params.error(error);
    });
}

export function put(route, params = {}) {
  Log.debug('API: PUT to ' + route.path + ' query: ' + stringifyData(params.query || '') + ' data: ' + stringifyData(params.data || ''));
  return axios.put(route.path, params.data, {
    params: params.query,
  })
    .then((response) => {
      // success callback
      if (params.success) {
        params.success(response.data);
      }
      return response;
    })
    .catch((error) => {
      Log.error('Something went wrong with the PUT call to ' + route.path + ' :' + stringifyError(error));
      // error callback
      if (params.error) params.error(error);
    });
}

// method is named 'destroy' on purpose since it is not possible to use 'delete'
export function destroy(route, params = {}) {
  Log.debug('API: delete to ' + route.path + ' query: ' + stringifyData(params.query || '') + ' data: ' + stringifyData(params.data || ''));
  return axios.delete(route.path, {
    data: params.data || {}, // hack to keep Content-Type header in request: https://github.com/mzabriskie/axios/issues/86#issuecomment-139638284
    params: params.query,
    // Statuses the caller handles itself (e.g. a template's 404: already deleted), or a function, as in get(): no report
    ['api-handled']: params.handledStatuses, // eslint-disable-line
  })
    .then((response) => {
      // success callback
      if (params.success) {
        params.success(response.data);
      }
      return response;
    })
    .catch((error) => {
      Log.error('Something went wrong with the DELETE call to ' + route.path + ' :' + stringifyError(error));
      // error callback
      if (params.error) params.error(error);
    });
}

export function patch(route, params = {}) {
  Log.debug('API: PATCH to ' + route.path + ' query: ' + stringifyData(params.query || '') + ' data: ' + stringifyData(params.data || ''));
  return axios.patch(route.path, params.data, {
    // Optional: the caller sends it again when no answer comes (the draft's autosave), so that failure is only a breadcrumb
    ['api-handles-no-response']: params.handlesNoResponse, // eslint-disable-line
    // Statuses the caller handles itself, or a function of the status and the API's answer for a status that means
    // several things (Alterar senha's empty 400: a wrong current password): no report
    ['api-handled']: params.handledStatuses, // eslint-disable-line
    params: params.query,
    // Optional: a limit in ms, as in get() (ETIMEDOUT when it runs out)
    ...(params.timeout ? { timeout: params.timeout, transitional: { clarifyTimeoutError: true } } : {}),
  })
    .then((response) => {
      // success callback
      if (params.success) {
        params.success(response.data);
      }
      return response;
    })
    .catch((error) => {
      Log.error('Something went wrong with the PATCH call to ' + route.path + ' :' + stringifyError(error));
      // error callback
      if (params.error) params.error(error);
    });
}

const t = (key: string) => globalTOrPtBr(key);

// axios codes for a request sent but never answered: offline, timed out, aborted or canceled.
// Errors thrown before sending (e.g. a body JSON.stringify can't handle) have none of them.
const NO_RESPONSE_CODES = ['ERR_NETWORK', 'ECONNABORTED', 'ETIMEDOUT', 'ERR_CANCELED'];
const wasSent = (error) => Boolean(error.response) || NO_RESPONSE_CODES.includes(error.code);

// Cut off before an answer came (by the browser, the OS or a reload). A request given a timeout (only the
// start's user and clinics, through get() above, and the record editor's switch in Ajustes, through patch())
// reports running out of it as ETIMEDOUT, and the others have none, so ECONNABORTED is always an abort.
const wasAborted = (error) => ['ECONNABORTED', 'ERR_CANCELED'].includes(error.code);

// Sent, and no answer came back: a dropped connection, Wi-Fi switching, a laptop waking up, or the
// browser cutting the request off
export const unanswered = (error: Object): boolean => wasSent(error) && !error.response;
const isRead = (config: Object) => String(config.method).toLowerCase() === 'get';

const requestRoute = (config: Object) => `${String(config.method || '').toUpperCase()} ${routeTemplate(config.url)}`;

// What the user is told when the API sent no message of its own: what went wrong, in their
// language, with the status so support can find it in Bugsnag. It used to be "No API response."
const genericErrorMessage = (error: Object, status: any): string => {
  if (!wasSent(error)) return t('error/api/request');
  if (!error.response) return t(window.navigator.onLine === false ? 'error/request_internet' : 'error/api/no_response');
  let text;
  if (status === 503) text = t('error/api/busy');
  else if (status >= 500) text = t('error/api/server');
  else if (status === 404) text = t('error/api/not_found');
  else if (status === 403) text = t('error/api/forbidden');
  else if (status === 401) text = t('error/api/unauthorized');
  else text = t('error/api/request');
  return `${text} ${globalT('error/api/code', `(erro ${status})`, { status })}`;
};

// A read left unanswered this long is likely CloudFront giving up on the API (its origin timeout),
// which looks like a dropped connection to the browser; a dropped connection usually fails sooner
const SLOW_UNANSWERED_MS = 25000;

// How many times the page went hidden (a tab in the background, a laptop's lid closing; the app in the background,
// which Cordova also tells with 'pause'), so a failed request can tell whether that happened while it was on its way.
// Counted from initialize().
let timesHidden = 0;
let countingHidden = false;
const countTimesHidden = () => {
  if (countingHidden) return;
  countingHidden = true;
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) timesHidden += 1;
  });
  document.addEventListener('pause', () => { timesHidden += 1; });
};

// A clock the system can't move (as appRecovery's monotonicNow; not imported, to keep this file's imports light). Where
// it stops while the device sleeps (macOS), the time asleep doesn't count either.
const monotonicNow = (): number => (window.performance && typeof window.performance.now === 'function' ? window.performance.now() : Date.now());

// A request left unanswered: how long it was on its way (elapsedMs by the monotonic clock, wallElapsedMs by the
// wall clock: a gap between them is the device asleep or its clock set), whether the page was hidden at any point
// meanwhile (hidden when sent, went hidden after, or hidden now), and whether the browser thinks it's online
const inFlight = (config: Object) => ({
  elapsedMs: config.sentAtMonotonic != null ? Math.round(monotonicNow() - config.sentAtMonotonic) : null,
  wallElapsedMs: config.sentAt ? Date.now() - config.sentAt : null,
  hiddenWhileInFlight: Boolean(config.sentHidden || document.hidden
    || (config.timesHiddenAtSend != null && timesHidden !== config.timesHiddenAtSend)),
  onLine: window.navigator.onLine,
});

// A failure the request's caller said it handles itself (the handledStatuses of Api.get, Api.destroy and Api.patch):
// a list of statuses, or a function of the status and what the API answered. Only asked about a failure the API
// answered (no answer is handlesNoResponse's).
const isHandled = (config: Object, status: any, data: any): boolean => {
  const handled = config['api-handled'];
  return typeof handled === 'function' ? Boolean(handled(status, data)) : includes(handled, status);
};

// What a failed request leaves in Bugsnag: 'report', 'breadcrumb', 'hidden', 'handled' or nothing. Every failure the
// user sees a generic message for is reported, so each one can be traced and fixed. That includes a save
// left unanswered or cut off (a change that may never have arrived) and a slow unanswered read.
// Not reported:
// - any other read left unanswered: the doctor's connection (a drop, Wi-Fi switching, a laptop
//   waking up). Bugsnag's own breadcrumb records it, and ours one the browser cut off, which it
//   doesn't. The API going down for everyone shows in Honeybadger's uptime check;
// - a slow read during which the page was hidden at some point (only a breadcrumb): a tab in the background or a
//   laptop asleep leaves a read unanswered that long without the API being slow;
// - a 401 (it logs out);
// - an API message for the user, like a 4xx business rule ("Você não pode trocar esse status.") or
//   the rate limiter's 503;
// - anything while offline (the user is told to check their internet, and Bugsnag couldn't send it);
// - a status its caller handles (only a breadcrumb), like getPatient's 404 for a patient of another clinic, or Alterar
//   senha's empty 400 (a wrong current password);
// - no answer to a request its caller sends again (only a breadcrumb; the second try, when it goes, is reported if it
//   fails too), like the draft's autosave (handlesNoResponse). A request cut off is still reported: it isn't sent again.
// `flight` is inFlight() for a request left unanswered.
const failureTrace = (error: Object, status: any, serverMessage: ?string, flight: ?Object): ?string => {
  if (window.navigator.onLine === false || status === 401) return null;
  if (error.response && isHandled(error.config || {}, status, error.response.data)) return 'handled';
  if (unanswered(error)) {
    const config = error.config || {};
    if (config['api-handles-no-response'] && !wasAborted(error)) return 'handled';
    if (!isRead(config)) return 'report';
    if (wasAborted(error)) return 'breadcrumb';
    const { elapsedMs, hiddenWhileInFlight } = flight || {};
    if ((elapsedMs || 0) < SLOW_UNANSWERED_MS) return null;
    return hiddenWhileInFlight ? 'hidden' : 'report';
  }
  return !serverMessage || (status >= 500 && status !== 503) ? 'report' : null;
};

// Bugsnag sends at most 10 events per screen (index.js restarts the count on each one), crashes
// included. Failed requests take at most half, each failure once, so a bad connection can't use
// up the reports a crash needs.
const MAX_REPORTS_PER_SCREEN = 5;
let reportedOnScreen = { screen: null, groups: [] };
const takeReportSlot = (group: string): boolean => {
  const screen = window.location.hash;
  if (reportedOnScreen.screen !== screen) reportedOnScreen = { screen, groups: [] };
  const { groups } = reportedOnScreen;
  if (groups.length >= MAX_REPORTS_PER_SCREEN || groups.includes(group)) return false;
  groups.push(group);
  return true;
};

const reportFailure = (error: Object, status: any, serverMessage: ?string, flight: ?Object) => {
  const route = requestRoute(error.config || {});
  // Grouped by what failed, not by the text shown: "404 Error GET /patients/:id/records".
  // An error thrown before sending is an app bug, so it goes as itself, grouped by its stack.
  const sent = wasSent(error);
  const reportError = sent ? new Error(route) : error;
  if (sent) reportError.name = error.response ? `${status} Error` : `${wasAborted(error) ? 'Aborted' : 'No response'} Error`;
  const group = `${reportError.name} ${route}`;
  if (!takeReportSlot(group)) return;

  const metadata = requestMetadata(error);
  // What the API said about a 5xx, which the generic message the user saw leaves out
  if (serverMessage) metadata.serverMessage = serverMessage;
  // No answer: how long it waited, and whether the page was hidden meanwhile
  if (flight) Object.assign(metadata, flight);

  Bugsnag.notify(reportError, event => {
    if (sent) event.groupingHash = group;
    event.addMetadata('request_data', metadata);
  });
};

// For a request whose error callback handled its failure: `.catch(ignoreApiFailure)` ends the API failure there,
// while an error thrown by the app's own code (in a callback, a reducer) still goes on to Bugsnag
export const ignoreApiFailure = (error: any) => {
  if (!error || !error.isApiFailure) throw error;
};

// For Bugsnag's onError. The interceptor below already reported each failed request (or
// deliberately didn't); its rejection left unhandled would report the same failure again.
export const isUnhandledApiFailure = (event: Object) => Boolean(event.unhandled && _get(event, ['originalError', 'isApiFailure']));

let appStore = null;

// The review mode for links built outside React (the dashboard's)
const inStoreReview = (): boolean => Boolean(appStore) && isStoreReview(appStore.getState());

function initialize(store) {
  appStore = store;
  countTimesHidden();
  const initState = store.getState();

  if (initState.auth.token) {
    axios.defaults.headers['access-token'] = initState.auth.token;
  }

  // REQUEST:
  // - send authorization on every request
  // - redirect on login page on unauthorized requests
  // Synchronous, so it reads the clinic when the request is made: run later, it could find the store
  // reset (a clinic switch, a forced reload, a store past 2 MB) and send no clinicId, which the API
  // refuses with "Você não faz mais parte desta clínica" (wrong_clinic). axios runs the chain
  // synchronously only when every request interceptor is, so any new one must be too.
  axios.interceptors.request.use(
    (config) => {
      const isApiCall = true; // TODO: add real api call check here
      if (isApiCall) {
        const state = store.getState();
        config.headers['user-date'] = moment().format('YYYY-MM-DD');
        config.headers['app-version'] = window.APP_VERSION;
        config.headers['app-platform'] = window.isCordovaApp ? 'cordova' : 'web';
        const interceptClinicId = _get(state, ['context', 'clinics', 'activeClinic', 'id']);
        if (interceptClinicId) {
          config.headers.clinicId = interceptClinicId && String(interceptClinicId) !== 'undefined' ? interceptClinicId : null;
        }
      }
      config.sentAt = Date.now();
      config.sentAtMonotonic = monotonicNow();
      config.sentHidden = document.hidden;
      config.timesHiddenAtSend = timesHidden;
      return config;
    },
    // Rethrown, so the request fails as it did before instead of going out without these headers
    (error) => {
      Log.error('Request ERROR', stringifyError(error));
      throw error;
    },
    { synchronous: true },
  );

  // RESPONSE:
  //  - log
  //  - go to /login if unauthorized (i.e. we are not logged in at all)
  axios.interceptors.response.use((response) => {
    const appVersion = response.headers['app-version'];

    if (response.headers['access-token']) {
      const state = store.getState();

      const oldToken = _get(state, ['auth', 'token']);
      const newToken = response.headers['access-token'];

      if (oldToken !== newToken) {
        store.dispatch({ type: actionTypes.AUTH_LOGGED_IN, token: newToken });
      }
    }

    if (appVersion) {
      const state = store.getState();
      const storedStateVersion = _get(state, ['appVersion', 'version']);
      const versions = JSON.parse(appVersion);

      const storeReview = isStoreReviewBuild(versions.ios_review);
      if (storeReview !== Boolean(_get(state, ['appVersion', 'storeReview']))) {
        store.dispatch(appVersionActions.setStoreReview(storeReview));
      }

      const iosPlatformInstances = ['iPhone', 'iPad'];
      let platform;
      if (!window.isCordovaApp) {
        platform = 'web';
      } else if (window.navigator && (indexOf(iosPlatformInstances, window.navigator.platform) > -1)) {
        platform = 'ios';
      } else {
        platform = 'android';
      }

      const platformAppVersion = versions[platform];
      const storedStateVersionChunks = ((storedStateVersion && /^\d*\.\d*\.\d*$/.test(storedStateVersion + '')) ? storedStateVersion.split('.').map(Number) : [0, 0, 0]);
      const platformAppVersionChunks = platformAppVersion.split('.').map(Number);

      const appVersionControlOpened = _get(state, ['appVersion', 'notification', 'active']);
      if ((platformAppVersionChunks[0] > storedStateVersionChunks[0]) || ((platformAppVersionChunks[0] === storedStateVersionChunks[0]) && (platformAppVersionChunks[1] > storedStateVersionChunks[1]))) {
        const data = {
          newVersion: platformAppVersion,
          type: 'main',
          platform,
        };
        store.dispatch(appVersionActions.openAppVersionControlNotification(data));
      } else if ((platformAppVersionChunks[0] === storedStateVersionChunks[0]) && (platformAppVersionChunks[1] === storedStateVersionChunks[1]) && (platformAppVersionChunks[2] > storedStateVersionChunks[2])) {
        const data = {
          newVersion: platformAppVersion,
          type: 'minor',
          platform,
        };
        store.dispatch(appVersionActions.openAppVersionControlNotification(data));
      } else if (appVersionControlOpened) {
        store.dispatch(appVersionActions.closeAppVersionControlNotification());
      }
    }

    const remoteRefreshRequested = response.headers['force-reload']
      ? JSON.parse(response.headers['force-reload'])
      : false;

    if (remoteRefreshRequested) {
      store.dispatch({ type: appControlTypes.UPDATE_APP });
    }

    return response;
  },
  (error) => {
    const { data, status = 'No Status' } = error.response || {};
    const config = error.config || {};
    const firstError = _get(data, ['errors', 0]);
    const property = _get(firstError, 'property', '');
    const errorMsg = _get(firstError, ['errorMessages', 0], '');
    // Rails' own error response ({ status: 400, error: 'Bad Request' }) is no message for the user
    const isRailsErrorResponse = _get(data, 'status') === status && Object.keys(data).length === 2;
    const serverMessage = errorMsg || (isRailsErrorResponse ? null : _get(data, ['error']));
    // The API's message when it sent one; otherwise what went wrong, in the user's language
    const message = serverMessage
      ? (property && errorMsg ? `${property} ${errorMsg}` : serverMessage)
      : genericErrorMessage(error, status);
    Log.error('Response ERROR', message, `(Status: ${status})`);

    if (status === 401) store.dispatch(logout(false));

    // Read once, so what decides the trace is what Bugsnag gets
    const flight = unanswered(error) ? inFlight(config) : null;
    const trace = failureTrace(error, status, serverMessage, flight);
    if (trace === 'report') reportFailure(error, status, serverMessage, flight);
    if (trace === 'breadcrumb') Bugsnag.leaveBreadcrumb(`Cut off: ${requestRoute(config)}`, {}, 'request');
    if (trace === 'hidden') Bugsnag.leaveBreadcrumb(`Unanswered while hidden: ${requestRoute(config)}`, flight, 'request');
    // A request left unanswered says how long it waited, so the report of its second try shows the first one's too
    if (trace === 'handled') Bugsnag.leaveBreadcrumb(`Handled ${error.response ? status : 'no response'}: ${requestRoute(config)}`, flight || {}, 'request');

    // Redirect on status code 404 if fallback route is set. getRoutePath takes the route's name (given a path,
    // it made "-path for … not found-"), and replace keeps the screen that got the 404 out of Back's way
    if (status === 404 && config['api-fallback'] && !isHandled(config, status, data)) {
      const fallbackRoute = typeof config['api-fallback'] === 'string' ? config['api-fallback'] : 'notFound';
      store.dispatch(replace(router.getRoutePath(fallbackRoute)));
    }

    const errorDt = new Error(message);
    errorDt.raw = _get(data, ['errors'], []) || [];
    errorDt.isApiFailure = true;
    // What the API answered, for screens that show its errors themselves (a code, errors per field)
    errorDt.status = error.response ? status : null;
    errorDt.data = data;
    // Sent and never answered, without being cut off: the connection dropped, and the request may be sent again
    errorDt.unanswered = unanswered(error) && !wasAborted(error);
    // reject promise to trigger catch(error) within the call function (e.g. GET)
    return Promise.reject(errorDt);
  });

  // Never include cookies in API requests,
  // to be consistent with the behaviour of modern browsers.
  // See https://app.asana.com/0/1115564467030975/1134669226797567
  axios.defaults.withCredentials = false;
  axios.defaults.credentials = 'omit';
}

export default {
  get,
  post,
  destroy,
  put,
  patch,
  initialize,
  inStoreReview,
};
